// src/pages/Login.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

/* ================================================================== */
/* Hero background image                                               */
/* ================================================================== */

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=1200&q=80';

/* ================================================================== */
/* Login                                                               */
/* ================================================================== */

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Reset password modal
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const emailInputRef = useRef(null);
  const mountedRef = useRef(true);
  const resetAutoCloseTimerRef = useRef(null);
  const navigate = useNavigate();

  /* ---------------- Mount/unmount guard ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (resetAutoCloseTimerRef.current) {
        clearTimeout(resetAutoCloseTimerRef.current);
        resetAutoCloseTimerRef.current = null;
      }
    };
  }, []);

  /* ---------------- On mount: restore remembered email ---------------- */
  useEffect(() => {
    const remembered = localStorage.getItem('rememberedEmail');
    if (remembered) {
      setEmail(remembered);
      setRememberMe(true);
      setTimeout(() => document.getElementById('password')?.focus(), 100);
    } else {
      emailInputRef.current?.focus();
    }
  }, []);

  /* ---------------- Clear error on input change ---------------- */
  useEffect(() => {
    setError('');
  }, [email, password]);

  /* ---------------- Lock body scroll while reset modal is open ---------------- */
  useEffect(() => {
    if (!resetModalOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [resetModalOpen]);

  /* ---------------- Escape closes reset modal ---------------- */
  useEffect(() => {
    if (!resetModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !resetLoading) setResetModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [resetModalOpen, resetLoading]);

  /* ================================================================== */
  /* Post-login redirect                                                 */
  /* ================================================================== */

  const completeLogin = async (userData) => {
    try {
      // Update last-login timestamp now that we're fully authenticated
      if (auth.currentUser?.uid) {
        updateDoc(doc(db, 'users', auth.currentUser.uid), {
          lastLoginAt: serverTimestamp(),
          lastSeen: serverTimestamp(),
        }).catch(() => {
          /* best-effort; ignore */
        });
      }

      const isAdmin = userData?.isAdmin === true;
      const role = (userData?.role || 'member').toLowerCase();

      if (isAdmin) {
        navigate('/admin');
      } else if (role === 'board') {
        navigate('/');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Login completion error:', err);
      if (mountedRef.current) setError('Login failed. Please try again.');
    }
  };

  /* ================================================================== */
  /* Main login submit                                                   */
  /* ================================================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const trimmedEmail = email.trim();

    try {
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', trimmedEmail);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      const userCred = await signInWithEmailAndPassword(
        auth,
        trimmedEmail,
        password,
      );
      const user = userCred.user;

      let userDoc;
      try {
        userDoc = await getDoc(doc(db, 'users', user.uid));
      } catch (fsErr) {
        // Firestore read failed — sign out so the user isn't half-authenticated
        console.error('Firestore user read failed:', fsErr);
        await auth.signOut();
        if (mountedRef.current) {
          setError('Could not load your account. Please try again.');
        }
        return;
      }

      if (!userDoc.exists()) {
        if (mountedRef.current) {
          setError('Account setup incomplete. Please contact support.');
        }
        await auth.signOut();
        return;
      }

      const userData = userDoc.data();

      const isApproved =
        userData.approved === true ||
        userData.isApproved === true ||
        userData.registrationStatus === 'approved';

      /**
       * Pending check: only count as pending if the account hasn't
       * been approved OR explicitly rejected yet.
       *
       * Previously this was `!isApproved && approved === false`, which
       * meant that if an admin flipped `registrationStatus` to
       * `'approved'` without also flipping `approved: true`, users got
       * bounced to /approval-pending forever. Using `registrationStatus`
       * as the source of truth here means the status field always wins.
       */
      const isRejected = userData.registrationStatus === 'rejected';
      const isPending =
        !isApproved &&
        !isRejected &&
        userData.registrationStatus !== 'approved';
      const isActive = userData.isActive !== false;
      const isBanned =
        userData.isBanned === true || userData.isBlocked === true;

      // Gate order: banned → rejected → inactive → pending → approved.
      // Most severe / most informative state wins.
      if (isBanned) {
        if (mountedRef.current) {
          setError('Your account has been banned. Please contact support.');
        }
        await auth.signOut();
        return;
      }

      if (isRejected) {
        const reason = userData.rejectionReason || 'No reason provided';
        if (mountedRef.current) {
          setError(
            `Your registration was rejected. Reason: ${reason}. Please contact an administrator.`,
          );
        }
        await auth.signOut();
        return;
      }

      if (!isActive) {
        if (mountedRef.current) {
          setError(
            'Your account has been deactivated. Please contact an administrator.',
          );
        }
        await auth.signOut();
        return;
      }

      if (isPending) {
        navigate('/approval-pending');
        return;
      }

      await completeLogin(userData);
    } catch (err) {
      console.error('Login error:', err);
      let errorMessage = 'Login failed. Please check your credentials.';
      switch (err.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled. Contact support.';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email or password.';
          break;
        default:
          break;
      }
      if (mountedRef.current) setError(errorMessage);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  /* ================================================================== */
  /* Reset password                                                      */
  /* ================================================================== */

  const openResetModal = () => {
    setResetSent(false);
    setResetError('');
    setResetEmail(email.trim());
    setResetModalOpen(true);
  };

  const handleResetPassword = async () => {
    const trimmed = resetEmail.trim();
    if (!trimmed) {
      setResetError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setResetError('Please enter a valid email address');
      return;
    }

    setResetLoading(true);
    setResetError('');
    try {
      await sendPasswordResetEmail(auth, trimmed, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      });
      if (!mountedRef.current) return;
      setResetSent(true);
      // Clear any previous auto-close timer, then set a new one
      if (resetAutoCloseTimerRef.current) {
        clearTimeout(resetAutoCloseTimerRef.current);
      }
      resetAutoCloseTimerRef.current = setTimeout(() => {
        resetAutoCloseTimerRef.current = null;
        if (!mountedRef.current) return;
        setResetModalOpen(false);
        setResetSent(false);
        setResetEmail('');
      }, 4000);
    } catch (err) {
      let message = 'Failed to send reset email. Please try again.';
      if (err.code === 'auth/user-not-found')
        message = 'No account found with this email address.';
      if (err.code === 'auth/invalid-email')
        message = 'Invalid email address format.';
      if (err.code === 'auth/too-many-requests')
        message = 'Too many requests. Please try again later.';
      if (mountedRef.current) setResetError(message);
    } finally {
      if (mountedRef.current) setResetLoading(false);
    }
  };

  /* ================================================================== */
  /* Render                                                              */
  /* ================================================================== */

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">
      {/* ============ LEFT: Hero background (desktop only) ============ */}
      <div
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-orange/80 via-orange-dark/70 to-black/80" />
        <div className="absolute inset-0 bg-black/30" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">
          <div className="flex items-center gap-3">
            <img
              src="/images/iiee-logo.png"
              alt="IIEE-ERCSA"
              className="h-12 w-12 object-contain"
            />
            <div>
              <h2 className="text-lg font-bold">IIEE-ERCSA</h2>
              <p className="text-xs text-white/70">Eastern Region Chapter</p>
            </div>
          </div>

          <div className="max-w-lg">
            <h1 className="text-4xl xl:text-5xl font-bold mb-4 leading-tight">
              Welcome back to your{' '}
              <span className="text-orange">community</span>
            </h1>
            <p className="text-base xl:text-lg text-white/80 mb-8">
              Connect with fellow electrical engineers, share moments, and stay
              updated with the IIEE-ERCSA network.
            </p>

            <ul className="space-y-3">
              <FeatureBullet icon="fa-images" text="Share photos and stories with members" />
              <FeatureBullet icon="fa-heart" text="Like, comment, and engage with the community" />
              <FeatureBullet icon="fa-comments" text="Direct messaging and video calls" />
            </ul>
          </div>

          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} IIEE-ERCSA · Eastern Region Chapter
          </p>
        </div>
      </div>

      {/* ============ RIGHT: Login form ============ */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center px-4 sm:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden text-center mb-6">
            <img
              src="/images/iiee-logo.png"
              alt="IIEE-ERCSA"
              className="h-14 mx-auto mb-3"
            />
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                Welcome Back
              </h1>
              <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                Sign in to your account
              </p>
            </div>

            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  role="alert"
                  className={`mb-4 p-3 border rounded-lg text-sm ${
                    error.includes('pending approval') ||
                    error.includes('Pending')
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-400 text-yellow-800 dark:text-yellow-200'
                      : 'bg-red-100 dark:bg-red-900/30 border-red-400 text-red-700 dark:text-red-300'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <i
                      className={`fas ${
                        error.includes('pending approval') ||
                        error.includes('Pending')
                          ? 'fa-clock'
                          : error.includes('rejected')
                          ? 'fa-times-circle'
                          : 'fa-exclamation-circle'
                      } mt-0.5`}
                      aria-hidden="true"
                    />
                    <div className="whitespace-pre-line">{error}</div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Email Address
                </label>
                <input
                  ref={emailInputRef}
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent dark:bg-gray-700 dark:text-white transition"
                  required
                  autoComplete="email"
                  disabled={loading}
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent dark:bg-gray-700 dark:text-white transition pr-10"
                    required
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-orange transition"
                  >
                    <i
                      className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}
                      aria-hidden="true"
                    />
                  </button>
                </div>

                <div className="flex justify-between items-center mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-3.5 h-3.5 accent-orange"
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      Remember me
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={openResetModal}
                    className="text-xs text-orange hover:underline focus:outline-none transition font-medium"
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange hover:bg-orange-dark text-white font-semibold py-2.5 rounded-lg transition transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Sign In
                    <i className="fas fa-arrow-right text-sm" aria-hidden="true" />
                  </span>
                )}
              </button>
            </form>

            {/* Info box */}
            <div className="mt-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-2">
                <i
                  className="fas fa-info-circle text-blue-500 text-sm mt-0.5"
                  aria-hidden="true"
                />
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  <p className="font-semibold text-blue-700 dark:text-blue-300 mb-1">
                    New Member?
                  </p>
                  <p className="mb-2">
                    All new registrations require admin approval before full access.
                    While pending, you can:
                  </p>
                  <ul className="space-y-1">
                    <li className="flex items-center gap-1.5">
                      <i className="fas fa-check text-green-500 text-[10px]" aria-hidden="true" />
                      Log in and complete your profile
                    </li>
                    <li className="flex items-center gap-1.5">
                      <i className="fas fa-check text-green-500 text-[10px]" aria-hidden="true" />
                      Upload a profile photo
                    </li>
                    <li className="flex items-center gap-1.5">
                      <i className="fas fa-times text-red-400 text-[10px]" aria-hidden="true" />
                      Access chat &amp; full feed (after approval)
                    </li>
                  </ul>
                  <p className="mt-2">
                    You'll be notified by email once approved. Usually within 24–48
                    hours.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-700">
              <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="text-orange hover:underline font-semibold transition"
                >
                  Register Here
                </Link>
              </p>
              <p className="text-center text-xs text-gray-500 dark:text-gray-500 mt-3">
                By signing in, you agree to our Terms of Service and Privacy Policy.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* ================================================================== */}
      {/* Reset Password Modal                                                */}
      {/* ================================================================== */}
      <AnimatePresence>
        {resetModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => {
              if (!resetLoading) setResetModalOpen(false);
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="reset-title"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3
                  id="reset-title"
                  className="text-xl font-bold text-gray-800 dark:text-white"
                >
                  Reset Password
                </h3>
                <button
                  type="button"
                  onClick={() => setResetModalOpen(false)}
                  aria-label="Close"
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition"
                  disabled={resetLoading}
                >
                  <i className="fas fa-times text-xl" aria-hidden="true" />
                </button>
              </div>

              {resetSent ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-4"
                >
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i
                      className="fas fa-check-circle text-green-500 text-4xl"
                      aria-hidden="true"
                    />
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 font-medium">
                    Password reset email sent!
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    We've sent a reset link to{' '}
                    <strong className="text-orange break-all">{resetEmail}</strong>
                  </p>
                  <p className="text-xs text-gray-400 mt-3">
                    Check your inbox and follow the instructions.
                  </p>
                </motion.div>
              ) : (
                <>
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 bg-orange/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-key text-orange text-2xl" aria-hidden="true" />
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      Enter your email address and we'll send you a link to reset
                      your password.
                    </p>
                  </div>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={resetEmail}
                    onChange={(e) => {
                      setResetEmail(e.target.value);
                      setResetError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleResetPassword();
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange dark:bg-gray-700 dark:text-white mb-4"
                    autoFocus
                    disabled={resetLoading}
                  />
                  {resetError && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="mb-3 p-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-sm"
                    >
                      <i className="fas fa-exclamation-circle mr-1" aria-hidden="true" />
                      {resetError}
                    </motion.div>
                  )}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleResetPassword}
                      disabled={resetLoading}
                      className="flex-1 bg-orange text-white py-2 rounded-lg hover:bg-orange-dark transition disabled:opacity-50"
                    >
                      {resetLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                          Sending...
                        </span>
                      ) : (
                        'Send Reset Link'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setResetModalOpen(false)}
                      disabled={resetLoading}
                      className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ================================================================== */
/* Small components                                                    */
/* ================================================================== */

const FeatureBullet = ({ icon, text }) => (
  <li className="flex items-center gap-3">
    <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center flex-shrink-0">
      <i className={`fas ${icon} text-white text-sm`} aria-hidden="true" />
    </div>
    <span className="text-sm text-white/90">{text}</span>
  </li>
);

export default Login;